import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { getSettings, updateSettings } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import Icon from '../components/Icon';
import './SettingsPage.css';

// NER-1386 (fase 2 / NER-1478): configuración del negocio (org).
// Editables (PUT parcial): businessName, timezone, branding{logoUrl,colorHex},
// paymentMethods[], defaultServiceDurationMin. whatsappTemplates = read-only
// (los gestiona Meta). phoneNumberId NO se expone (routing de infra).
const DEFAULT_COLOR = '#7e4fb9';

function SettingsPage() {
  const { t } = useTranslation();
  const { preference, setPreference, autoMode, setAutoMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);

  const [businessName, setBusinessName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [colorHex, setColorHex] = useState(DEFAULT_COLOR);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [newPayment, setNewPayment] = useState('');
  const [defaultServiceDurationMin, setDefaultServiceDurationMin] = useState('');
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  // Rule 4 (NER-2213): turnos abiertos por cliente. Default true (flexibilidad SaaS).
  // Guardamos las demás keys de appointment para no pisarlas en el PUT parcial.
  const [allowMultipleOpen, setAllowMultipleOpen] = useState(true);
  const [appointmentExtra, setAppointmentExtra] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await getSettings();
      const s = res?.settings || {};
      setBusinessName(s.businessName || '');
      setTimezone(s.timezone || '');
      setLogoUrl(s.branding?.logoUrl || '');
      setColorHex(s.branding?.colorHex || DEFAULT_COLOR);
      setPaymentMethods(Array.isArray(s.paymentMethods) ? s.paymentMethods : []);
      setDefaultServiceDurationMin(
        s.defaultServiceDurationMin != null ? String(s.defaultServiceDurationMin) : ''
      );
      setWhatsappTemplates(Array.isArray(s.whatsappTemplates) ? s.whatsappTemplates : []);
      const appt = s.appointment && typeof s.appointment === 'object' ? s.appointment : {};
      const { allowMultipleOpenPerCustomer, ...restAppt } = appt;
      setAllowMultipleOpen(allowMultipleOpenPerCustomer !== false); // default true
      setAppointmentExtra(restAppt);
    } catch (err) {
      console.error('Error loading settings:', err);
      setLoadError(t('settings.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const addPayment = () => {
    const v = newPayment.trim();
    if (!v || paymentMethods.includes(v)) return;
    setPaymentMethods((p) => [...p, v]);
    setNewPayment('');
  };

  const removePayment = (value) => {
    setPaymentMethods((p) => p.filter((m) => m !== value));
  };

  const submit = async (e) => {
    e.preventDefault();
    const name = businessName.trim();
    if (!name) {
      setSaveError(t('settings.nameRequired'));
      return;
    }
    let duration;
    if (defaultServiceDurationMin !== '') {
      duration = Number(defaultServiceDurationMin);
      if (!Number.isInteger(duration) || duration <= 0) {
        setSaveError(t('settings.durationInvalid'));
        return;
      }
    }
    setSaving(true);
    setSaveError('');
    setSaved(false);
    // PUT parcial: solo keys editables (whatsappTemplates / phoneNumberId NO se envían).
    const patch = {
      businessName: name,
      timezone: timezone.trim(),
      branding: { logoUrl: logoUrl.trim(), colorHex },
      paymentMethods,
    };
    if (duration != null) patch.defaultServiceDurationMin = duration;
    patch.appointment = { ...appointmentExtra, allowMultipleOpenPerCustomer: allowMultipleOpen };
    try {
      const res = await updateSettings(patch);
      if (res?.settings?.whatsappTemplates) setWhatsappTemplates(res.settings.whatsappTemplates);
      setSaved(true);
    } catch (err) {
      console.error('Error saving settings:', err);
      setSaveError(t('settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>{t('settings.title')}</h1>
      </div>
      <p className="settings-subtitle">{t('settings.subtitle')}</p>

      {loadError && <div className="error-message">{loadError}</div>}

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <form className="settings-form" onSubmit={submit}>
          {/* Apariencia (NER-1564): tema claro/oscuro/auto + sub-modo de auto */}
          <section className="card settings-section">
            <h2 className="settings-section-title">{t('theme.appearance')}</h2>
            <p className="setting-description">{t('theme.appearanceDescription')}</p>
            <div className="theme-selector" role="radiogroup" aria-label={t('theme.title')}>
              <button
                type="button"
                className={`theme-option ${preference === 'light' ? 'active' : ''}`}
                role="radio"
                aria-checked={preference === 'light'}
                onClick={() => setPreference('light')}
              >
                <Icon name="light_mode" />
                <span>{t('theme.light')}</span>
              </button>
              <button
                type="button"
                className={`theme-option ${preference === 'dark' ? 'active' : ''}`}
                role="radio"
                aria-checked={preference === 'dark'}
                onClick={() => setPreference('dark')}
              >
                <Icon name="dark_mode" />
                <span>{t('theme.dark')}</span>
              </button>
              <button
                type="button"
                className={`theme-option ${preference === 'auto' ? 'active' : ''}`}
                role="radio"
                aria-checked={preference === 'auto'}
                onClick={() => setPreference('auto')}
              >
                <Icon name="brightness_auto" />
                <span>{t('theme.auto')}</span>
              </button>
            </div>
            {preference === 'auto' && (
              <div
                className="theme-selector theme-submode"
                role="radiogroup"
                aria-label={t('theme.autoModeLabel')}
              >
                <button
                  type="button"
                  className={`theme-option ${autoMode === 'system' ? 'active' : ''}`}
                  role="radio"
                  aria-checked={autoMode === 'system'}
                  onClick={() => setAutoMode('system')}
                >
                  <Icon name="computer" />
                  <span>{t('theme.autoModeSystem')}</span>
                </button>
                <button
                  type="button"
                  className={`theme-option ${autoMode === 'time-of-day' ? 'active' : ''}`}
                  role="radio"
                  aria-checked={autoMode === 'time-of-day'}
                  onClick={() => setAutoMode('time-of-day')}
                >
                  <Icon name="schedule" />
                  <span>{t('theme.autoModeTimeOfDay')}</span>
                </button>
              </div>
            )}
          </section>

          <section className="card settings-section">
            <h2 className="settings-section-title">{t('settings.businessSection')}</h2>
            <div className="form-group">
              <label htmlFor="set-name">{t('settings.businessName')} *</label>
              <input
                id="set-name"
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                maxLength={120}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="set-tz">{t('settings.timezone')}</label>
                <input
                  id="set-tz"
                  type="text"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  placeholder="America/Argentina/Buenos_Aires"
                />
              </div>
              <div className="form-group">
                <label htmlFor="set-duration">{t('settings.defaultDuration')}</label>
                <input
                  id="set-duration"
                  type="number"
                  min="1"
                  step="1"
                  value={defaultServiceDurationMin}
                  onChange={(e) => setDefaultServiceDurationMin(e.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="card settings-section">
            <h2 className="settings-section-title">{t('settings.appointmentSection')}</h2>
            <div className="settings-toggle-row">
              <div className="settings-toggle-text">
                <span className="settings-toggle-label">{t('settings.allowMultipleOpenLabel')}</span>
                <p className="setting-description">{t('settings.allowMultipleOpenDescription')}</p>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={allowMultipleOpen}
                  aria-label={t('settings.allowMultipleOpenLabel')}
                  onChange={(e) => setAllowMultipleOpen(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </section>

          <section className="card settings-section">
            <h2 className="settings-section-title">{t('settings.brandingSection')}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="set-logo">{t('settings.logoUrl')}</label>
                <input
                  id="set-logo"
                  type="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://…"
                />
              </div>
              <div className="form-group">
                <label htmlFor="set-color">{t('settings.brandColor')}</label>
                <input
                  id="set-color"
                  type="color"
                  className="settings-color"
                  value={colorHex}
                  onChange={(e) => setColorHex(e.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="card settings-section">
            <h2 className="settings-section-title">{t('settings.paymentSection')}</h2>
            {paymentMethods.length === 0 ? (
              <p className="settings-muted">{t('settings.noPaymentMethods')}</p>
            ) : (
              <ul className="settings-chip-list">
                {paymentMethods.map((m) => (
                  <li key={m} className="settings-chip">
                    <span>{m}</span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => removePayment(m)}
                      aria-label={t('common.delete')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="settings-add-row">
              <input
                type="text"
                value={newPayment}
                onChange={(e) => setNewPayment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addPayment();
                  }
                }}
                placeholder={t('settings.paymentPlaceholder')}
                maxLength={40}
              />
              <button type="button" className="btn btn-ghost btn-sm" onClick={addPayment}>
                <Plus size={15} />
                {t('settings.addPayment')}
              </button>
            </div>
          </section>

          <section className="card settings-section">
            <h2 className="settings-section-title">{t('settings.templatesSection')}</h2>
            <p className="settings-muted">{t('settings.templatesNote')}</p>
            {whatsappTemplates.length > 0 && (
              <ul className="settings-template-list">
                {whatsappTemplates.map((tpl, i) => (
                  <li key={tpl.name || tpl.id || i} className="settings-template">
                    <span className="settings-template-name">{tpl.name || tpl.id || tpl}</span>
                    {tpl.status && <span className="list-tag">{tpl.status}</span>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {saveError && <div className="error-message">{saveError}</div>}
          {saved && <div className="settings-saved">{t('settings.saved')}</div>}

          <div className="settings-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default SettingsPage;
